import {useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Button} from "primereact/button";
import {Booking} from "../data/Booking";
import {Card} from "primereact/card";
import {MinimalSlot} from "../data/Slot";
import {format} from "date-fns";
import {Toast} from "primereact/toast";
import logo from "./../buchmi.svg";
import {ProgressSpinner} from "primereact/progressspinner";

function SubmittedComponent() {
    const {uuid} = useParams<{ uuid: string }>();
    const navigate = useNavigate();
    const [slot, setSlot] = useState<MinimalSlot | null>(null);
    const [booking, setBooking] = useState<Booking | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const toast = useRef<Toast>(null);

    useEffect(() => {
        async function fetchBookingData() {
            try {
                const bookingResponse = await fetch(
                    `/api/booking/entries/${uuid}`,
                );

                if (!bookingResponse.ok) {
                    throw new Error("Buchung nicht gefunden");
                }

                const bookingData = await bookingResponse.json();
                const booking: Booking = Object.assign(new Booking(), bookingData);

                const slotResponse = await fetch(
                    `/api/slot/entries/${booking.slotId}`,
                );

                if (!bookingResponse.ok) {
                    throw new Error("Slot nicht gefunden");
                }

                const slotData = await slotResponse.json();
                const slot: MinimalSlot = Object.assign(new MinimalSlot(), slotData);

                setBooking(booking);
                setSlot(slot);
            } catch (err) {
            } finally {
                setLoading(false);
            }
        }

        if (uuid) {
            fetchBookingData();
        }
    }, [uuid]);

    const personString = booking?.amount === 1 ? "Person" : "Personen";

    const downloadICS = () => {
        if (!slot || !slot.startDateTime || !slot.endDateTime || !booking) {
            return;
        }

        const startTime = format(slot.startDateTime, "yyyyMMdd'T'HHmmss");
        const endTime = format(slot.endDateTime, "yyyyMMdd'T'HHmmss");
        const bookingUrl = `${window.location.origin}/booking/${booking.id}`;

        const icsContent = `
BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Moimio Booking//EN
BEGIN:VEVENT
UID:${booking.id}
DTSTAMP:${format(new Date(), "yyyyMMdd'T'HHmmss")}
DTSTART:${startTime}
DTEND:${endTime}
SUMMARY:Moimio Buchung: ${booking.name} für ${booking.amount} ${personString}
DESCRIPTION:Buchungslink: ${bookingUrl}
LOCATION:Moimio Untereggersberg 2, 93339 Riedenburg
ORGANIZER:mailto:info@moimio.de
END:VEVENT
END:VCALENDAR
    `;

        const blob = new Blob([icsContent], {type: "text/calendar"});
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Moimio_Buchung_${booking.id}.ics`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const onBackClicked = () => {
        navigate("/");
    };

    if (loading) {
        return (
            <div
                className="flex align-items-center justify-content-center"
                style={{height: "100vh"}}
            >
                <ProgressSpinner/>
            </div>
        );
    }

    if (!booking || !slot) {
        return (
            <div
                className="w-full mx-auto p-3 "
                style={{
                    maxWidth: "800px",
                    margin: "0 auto",
                    padding: "1rem",
                    boxSizing: "border-box",
                    textAlign: "center",
                }}
            >
                <Card
                    title="Buchung nicht gefunden"
                    className="w-full mb-5 border-round-lg bg-red-100"
                >
                    <div className="flex flex-column align-items-center justify-content-center">
                        <p className="m-0 line-height-3 text-center">
                            Es wurde keine Buchung mit der ID {uuid} gefunden.
                        </p>
                    </div>
                </Card>
                <div className="flex justify-content-center">
                    <Button
                        label="Zurück zur Startseite"
                        onClick={onBackClicked}
                        className="mt-2"
                    />
                </div>
            </div>
        );
    }

    console.log(slot.endDateTime);
    console.log(new Date());

    if (new Date(slot.endDateTime) < new Date()) {
        return (
            <div
                className="w-full mx-auto p-3 "
                style={{
                    maxWidth: "800px",
                    margin: "0 auto",
                    padding: "1rem",
                    boxSizing: "border-box",
                    textAlign: "center",
                }}
            >
                <Card
                    title="Termin ist bereits vorbei"
                    className="w-full mb-5 border-round-lg bg-yellow-100"
                >
                    <div className="flex flex-column align-items-center justify-content-center">
                        <p className="m-0 line-height-3 text-center">
                            Dieser Termin liegt in der Vergangenheit und ist nicht mehr
                            gültig.
                        </p>
                    </div>
                </Card>
                <div className="flex justify-content-center">
                    <Button
                        label="Zurück zur Startseite"
                        onClick={onBackClicked}
                        className="mt-2"
                    />
                </div>
            </div>
        );
    }

    return (
        <div
            className="w-full mx-auto p-3 "
            style={{
                maxWidth: "800px",
                margin: "0 auto",
                padding: "1rem", // Adds spacing around the calendar
                boxSizing: "border-box",
                textAlign: "center",
            }}
        >
            <div className="my-3 flex flex-column justify-content-center flex-wrap">
                <div>
                    <img src={logo} alt="Buchmi Logo" className="w-6rem "/>
                </div>
            </div>
            <Card
                title="Buchung erfolgreich"
                className="w-full mb-3 border-round-lg bg-green-100"
            >
                <p className="m-0 line-height-3">
                    Deine Buchung für den Zeitraum
                    <br/>
                    <strong>
                        {format(slot.startDateTime, "dd.MM.yyyy HH:mm")} -{" "}
                        {format(slot.endDateTime, "dd.MM.yyyy HH:mm")}
                    </strong>
                    <br/>
                    auf den Namen <br/>
                    <strong>{booking.name}</strong> für{" "}
                    <strong>
                        {booking.amount} {personString}
                    </strong>{" "}
                    <br/> wurde erfolgreich übermittelt.
                    <br/>
                </p>
                <p className="my-5 text-lg"> Ich freue mich auf dein Kommen!</p>
                <p className="text-sm">
                    {" "}
                    Zum Stornieren oder Termin-Änderungen kontaktiere mich bitte per
                    WhatsApp oder E-Mail.
                </p>
                <p className="text-xs text-secondary text-center mt-5 opacity-50 text-700">
                    Buchungs-ID: {booking.id}
                </p>
            </Card>

            <div className="flex flex-column gap-2">
                <Toast ref={toast}/>
                <Button
                    label="Teilen"
                    icon="pi pi-share-alt"
                    onClick={() => {
                        const bookingUrl = `${window.location.origin}/booking/${booking.id}`;
                        const shareData = {
                            title: `Moimio Buchung: ${booking.name}`,
                            text: `Buchung für den Zeitraum ${format(slot.startDateTime, "dd.MM.yyyy HH:mm")} - ${format(slot.endDateTime, "dd.MM.yyyy HH:mm")} auf den Namen ${booking.name} für ${booking.amount} ${personString}.\n${bookingUrl}`,
                        };
                        if (navigator.share) {
                            navigator
                                .share(shareData)
                                .catch((error) => console.error("Fehler beim Teilen:", error));
                        } else {
                            const bookingUrl = `${window.location.origin}/booking/${booking.id}`;
                            navigator.clipboard.writeText(bookingUrl).then(() => {
                                toast.current?.show({
                                    severity: "info",
                                    summary: "URL kopiert",
                                    detail:
                                        "Die Buchungs-URL wurde in die Zwischenablage kopiert.",
                                    life: 3000,
                                });
                            });
                        }
                    }}
                    className="p-button-secondary"
                />

                <Button
                    label="In Kalender übernehmen"
                    icon="pi pi-download"
                    onClick={downloadICS}
                    className="p-button-secondary"
                />
                <Button
                    label="Ausdrucken"
                    icon="pi pi-print"
                    onClick={() => window.print()}
                    className="p-button-secondary"
                />
                <Button
                    icon="pi pi-arrow-left"
                    label="Zurück"
                    onClick={onBackClicked}
                    className="mt-2"
                />
            </div>
        </div>
    );
}

export default SubmittedComponent;
